// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODES } from '@/api/constants';
import { z } from 'zod';

/**
 * Error code type definition
 */
export type ErrorCode =
  | 'date_range_exceeded'
  | 'sku_exists'
  | 'user_cannot_modify_self'
  | 'product_invalid_name'
  | 'product_archived_must_be_set'
  | 'product_field_must_exist'
  | 'product_field_label_required'
  | 'product_field_type_required'
  | 'product_field_section_required'
  | 'product_field_order_required'
  | 'product_invalid_id'
  | 'product_field_invalid_number'
  | 'product_field_invalid_date'
  | 'product_field_invalid_boolean'
  | 'product_field_invalid_text'
  | 'product_field_invalid_integer'
  | 'product_field_invalid_datetime'
  | 'product_field_invalid_time'
  | 'product_field_invalid_type'
  | 'product_field_invalid_upload'
  | 'product_field_invalid_serial_header'
  | 'product_field_invalid_header'
  | 'product_not_found'
  | 'product_has_assets'
  | 'product_key_required'
  | 'product_merge_same_product'
  | 'product_merge_field_conflict'
  | 'product_merge_inventory_conflict'
  | 'product_merge_crm_conflict'
  | 'item_merge_bulk_not_allowed'
  | 'item_invalid_type'
  | 'bom_only_device_items'
  | 'item_has_inventory_movements'
  | 'marked_bulk_to_unmarked_not_allowed'
  | 'cyclical_reference'
  | 'project_ends_before_it_starts'
  | 'project_name_required'
  | 'project_has_costs'
  | 'project_has_invoiced_charges'
  | 'project_line_item_override_below_invoiced'
  | 'project_workflow_cannot_delete_last_workflow'
  | 'project_workflow_cannot_delete_in_use_workflow'
  | 'invalid_csv_format'
  | 'organization_third_party_fulfillment_not_enabled'
  | 'organization_deactivated'
  | 'organization_access_ended'
  | 'organization_member_deactivated'
  | 'oauth_must_be_google_managed_org'
  | 'oauth_gmail_not_allowed'
  | 'oauth_email_must_be_verified'
  | 'oauth_domain_not_verified'
  | 'oauth_custom_domain_required'
  | 'oauth_email_not_found'
  | 'oauth_identity_mismatch'
  | 'location_not_third_party_enabled'
  | 'location_missing_third_party_identifier'
  | 'third_party_provider_not_found'
  | 'third_party_provider_unreachable'
  | 'third_party_logistics_disabled'
  | 'location_missing_address'
  | 'asset_move_destination_customer_mismatch'
  | 'asset_move_destination_required'
  | 'asset_move_delivery_date_required'
  | 'asset_move_delivery_date_in_future'
  | 'asset_resolve_serial_limit_exceeded'
  | 'asset_invalid_cost_adjustment_type'
  | 'asset_invalid_cost_adjustment_reason'
  | 'asset_invalid_cost_adjustment_amount'
  | 'asset_cost_adjustment_date_in_future'
  | 'asset_cost_adjustment_notes_required'
  | 'asset_cost_adjustment_date_required'
  | 'asset_cost_adjustment_exceeds_book_value'
  | 'asset_invalid_useful_life_revision_reason'
  | 'asset_useful_life_revision_date_in_future'
  | 'asset_useful_life_revision_notes_required'
  | 'asset_useful_life_revision_date_required'
  | 'asset_move_conflict'
  | 'asset_mixed_currency'
  | 'asset_move_limit_exceeded'
  | 'asset_move_ship_date_in_future'
  | 'asset_event_type_requires_event_group'
  | 'accounting_invalid_useful_life'
  | 'accounting_initial_true_up_already_exists'
  | 'accounting_initial_true_up_required'
  | 'accounting_initial_true_up_period_not_latest_locked_period'
  | 'accounting_initial_true_up_currency_has_no_assets'
  | 'accounting_close_run_missing_currency'
  | 'accounting_mixed_currency'
  | 'location_duplicate_name'
  | 'zone_name_matches_parent'
  | 'site_name_matches_zone'
  | 'location_merge_into_own_zone'
  | 'location_field_not_valid_for_type'
  | 'location_type_cannot_change'
  | 'location_not_found'
  | 'invalid_part_origin'
  | 'invalid_part_destination'
  | 'invalid_delivery_location'
  | 'invalid_shipment_location'
  | 'invalid_shipment_ship_location'
  | 'invoice_already_open'
  | 'invoice_already_draft'
  | 'invoice_has_settlements'
  | 'invoice_customer_not_found'
  | 'invoice_already_void'
  | 'invoice_not_voidable'
  | 'invoice_void_reason_required'
  | 'invoice_not_deletable'
  | 'invoice_not_payable'
  | 'payment_exceeds_balance'
  | 'invoice_missing_customer'
  | 'invoice_missing_due_date'
  | 'invoice_number_duplicate'
  | 'invoice_not_sendable'
  | 'invoice_sent_at_future'
  | 'charge_not_cancellable'
  | 'charge_not_markable_billable'
  | 'charge_not_markable_not_billable'
  | 'charge_not_revertable'
  | 'billing_plan_not_periodic'
  | 'billing_plan_not_pending'
  | 'billing_plan_has_locked_charges'
  | 'billing_plan_missing_schedule_config'
  | 'billing_plan_cascade_count_mismatch'
  | 'billing_plan_invalid_service_period'
  | 'charge_not_attachable_to_invoice'
  | 'billing_plan_activation_date_out_of_range'
  | 'charge_trigger_type_event_not_accepted'
  | 'charge_already_attached_to_invoice'
  | 'sfdc_location_not_linked'
  | 'sfdc_drift_field_not_monitored'
  | 'sfdc_drift_field_not_actionable'
  | 'sfdc_drift_field_pull_unsupported'
  | 'sfdc_drift_field_name_required'
  | 'sfdc_reference_account_cache_pending'
  | 'sfdc_reference_account_cache_corrupt'
  | 'currency_value_exceeds_maximum'
  | 'currency_unknown_iso'
  | 'currency_mixed_iso'
  | 'currency_division_by_zero'
  | 'currency_sub_cent_precision'
  | 'currency_missing_iso'
  | 'currency_invalid_payload'
  | 'currency_invalid_amount'
  | 'currency_zero_decimal_precision'
  | 'currency_not_supported'
  | 'project_mixed_currency'
  | 'invoice_mixed_currency'
  | 'asset_transaction_currency_mismatch'
  | 'currency_negative_amount'
  | 'hubspot_not_configured'
  | 'hubspot_invalid_pipeline'
  | 'hubspot_invalid_pipeline_stage'
  | 'customer_invalid_billing_contact_email'
  | 'third_party_provider_rejected'
  | 'shipment_cancelled'
  | 'shipment_locked_by_3pl'
  | 'shipment_location_locked'
  | 'shipment_order_finished'
  | 'shipment_order_locked'
  | 'shipment_received_at_in_future'
  | 'shipment_shipped_at_in_future'
  | 'shipment_delivered_at_in_future'
  | 'inventory_removal_exceeds_stock'
  | 'shipment_order_direction_mismatch'
  | 'quarantine_serial_exists'
  | 'quarantine_serial_held'
  | 'organization_invalid_serial_pattern'
  | 'membership_rules_invalid'
  | 'membership_project_has_no_window'
  | 'plan_limit_exceeded';

/**
 * An asset whose recorded movements conflict with a proposed move. Returned in the
 * details payload of a move conflict so every affected asset can be listed, not only
 * the one named in the message.
 */
const conflictingAssetSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
});

export type ConflictingAsset = z.infer<typeof conflictingAssetSchema>;

/**
 * One error a third-party logistics provider returned when it rejected a request.
 * Title names the problem and detail explains it, both in the provider's own words.
 */
const providerErrorSchema = z.object({
  id: z.string().optional(),
  status: z.string().optional(),
  code: z.string().optional(),
  title: z.string(),
  detail: z.string(),
  messageType: z.string().optional(),
});

export type ProviderError = z.infer<typeof providerErrorSchema>;

/**
 * The asset that already carries a serial a create or update was refused for.
 * Returned so a client can link to that asset rather than sending the user to search.
 */
const existingAssetSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
});

export type ExistingAsset = z.infer<typeof existingAssetSchema>;

/**
 * The item that already carries a SKU a create or update was refused for.
 */
const existingItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  sku: z.string(),
});

export type ExistingItem = z.infer<typeof existingItemSchema>;

// Define the error schemas
const errorSchema = z.union([
  z
    .object({
      statusCode: z.number(),
      relation: z.object({
        violationType: z.literal('UNIQUE_VIOLATION'),
        entityColumnId: z.string().optional(),
        entityType: z.string().optional(),
        entityValue: z.string().optional(),
      }),
      details: existingAssetSchema.optional(),
    })
    .transform(({ relation, details, ...error }) => ({
      ...error,
      type: relation.violationType,
      message: '',
      entityColumnId: relation.entityColumnId,
      entityType: relation.entityType,
      entityValue: relation.entityValue,
      existingAsset: details,
    })),
  z
    .object({
      statusCode: z.number(),
      relation: z.object({
        violationType: z.literal('INTERSECTION_VIOLATION'),
      }),
      details: z.array(conflictingAssetSchema).nullable().optional(),
    })
    .transform(({ relation, details, ...error }) => ({
      ...error,
      type: relation.violationType,
      message: '',
      conflictingAssets: details ?? [],
    })),
  z
    .object({
      statusCode: z.number(),
      relation: z.object({
        violationType: z.literal('RANGE_VIOLATION'),
      }),
    })
    .transform(({ relation, ...error }) => ({
      ...error,
      type: relation.violationType,
      message: '',
    })),
  z
    .object({
      statusCode: z.number(),
      error: z.string().optional(),
      details: z.array(providerErrorSchema),
    })
    .transform(({ statusCode, error, details }) => ({
      statusCode,
      type: ERROR_CODES[statusCode] ?? 'unknown',
      message: error ?? 'unknown error',
      providerErrors: details,
    })),
  z
    .object({
      statusCode: z.number(),
      error: z.string().optional(),
      details: existingAssetSchema,
    })
    .transform(({ statusCode, error, details }) => ({
      statusCode,
      type: ERROR_CODES[statusCode] ?? 'unknown',
      message: error ?? 'unknown error',
      existingAsset: details,
    })),
  z
    .object({
      statusCode: z.number(),
      error: z.string().optional(),
      details: existingItemSchema,
    })
    .transform(({ statusCode, error, details }) => ({
      statusCode,
      type: ERROR_CODES[statusCode] ?? 'unknown',
      message: error ?? 'unknown error',
      existingItem: details,
    })),
  z
    .object({ statusCode: z.number(), error: z.string().optional() })
    .transform(({ statusCode, error }) => {
      return {
        statusCode,
        type: ERROR_CODES[statusCode] ?? 'unknown',
        message: error ?? 'unknown error',
      };
    }),
]);

const errorListSchema = z
  .array(errorSchema)
  .nullable()
  .optional()
  .transform((errors) => errors ?? []);

export const responseMetadataSchema = z.object({
  requestId: z.string(),
  errors: errorListSchema,
});

export type ResponseMetadata = z.infer<typeof responseMetadataSchema>;

export const errorResponseSchema = z.object({
  metadata: responseMetadataSchema,
});

// Note: successResponseSchema and SuccessResponse are defined in schemas.ts

/**
 * Well-formed error structure for the centralized error system
 */
export interface WellFormedError {
  /** The original error code or type */
  errorType: string;
  /** Human-readable error message */
  message: string;
  /** HTTP status code */
  httpStatusCode: number;
  /** API status code */
  apiStatusCode: number;
  /** Whether this error was sent to Sentry */
  sentToSentry: boolean;
  /** Timestamp when error occurred */
  timestamp: Date;
  /** Whether this was a network error */
  isNetworkError: boolean;
  /** The column/field that caused a unique violation (e.g., "sku", "name") */
  entityColumnId?: string;
  /** The entity type for violation errors (e.g., "ITEM", "PRODUCT") */
  entityType?: string;
  /** The value that caused the violation */
  entityValue?: string;
  /** Assets whose recorded movements conflict with a proposed move */
  conflictingAssets?: ConflictingAsset[];
  /** Errors a third-party logistics provider returned when it rejected a request */
  providerErrors?: ProviderError[];
  /** The asset already carrying a serial a create was refused for */
  existingAsset?: ExistingAsset;
  /** The item already carrying a SKU a create or update was refused for */
  existingItem?: ExistingItem;
}

/**
 * Zod schema for RedactedField that handles the different status cases
 */
export const redactedFieldSchema = <T>(
  valueSchema: z.ZodType<T, z.ZodTypeDef, unknown>
) =>
  z
    .object({
      status: z.enum(['REMOVED', 'VISIBLE']),
      value: z.union([valueSchema, z.null(), z.string()]), // Allow string for redacted values
    })
    .transform((data): T | null => {
      // Right now we are going to transform this to its underlying type from the base value
      // The status decoration is more for API consumers, the FE will parse the policy to determine what to do with the value
      return data.value as T | null;
    });
